import { FontWeight, LiliDropdown, Text } from '@lili/lili-frontend-common';
import { useCallback, useMemo, useState } from 'react';
import { t } from 'i18next';
import { useBlocker } from '@tanstack/react-router';
import { useDebounceHandler } from '@lili/utils/hooks';
import logoUrl from '@Assets/logos/lili-logo-blue.svg';
import greenCheckUrl from '@Assets/icons/ic-green-check.svg';
import greyXUrl from '@Assets/icons/ic-grey-x.svg';

import { BUSINESS_FILE_TYPES, getBusinessFileTypesImages } from '@Store/slices/kyb/kyb.types';
import { useGetBusinessOwners } from '@Pages/happyFlowPages/ownersCenter/hooks/useGetBusinessOwnersData';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { useVerificationCenterHook } from './logic/useVerificationCenterHook';
import { VerFooter } from './ui/VerFooter';
import { VerificationCenterHeader } from './ui/VerificationCenterHeader';
import { InfoSideBar } from './ui/InfoSideBar';
import { IntroScreen } from './ui/IntroScreen';
import { FileUpload, FileUploadError, FileUploadFileType } from './ui/FileUpload';
import { useFileManagement } from './logic/useFileManagement';
import { useVerificationCenterAnalytics } from './VerificationCenterAnalytics';
import type { LiliDropdownItem } from '@lili/lili-frontend-common';

import type React from 'react';

const maxFileSizeInMb = 15;

const documentTypeOptionsKeyByFileType: Partial<Record<keyof typeof BUSINESS_FILE_TYPES, string>> =
  {
    [BUSINESS_FILE_TYPES.UTILITY_BILL]: 'pages.verificationCenter.documentTypeOptions',
    [BUSINESS_FILE_TYPES.PROOF_OF_OWNERSHIP]:
      'pages.verificationCenter.proofOfOwnershipDocumentTypeOptions',
  };

const VerificationCenter: React.FC = () => {
  useBlocker({
    shouldBlockFn: ({ next }) => {
      const allowedRoutes: string[] = [routesModel[RoutesMap.UNDER_REVIEW].path];
      return !allowedRoutes.includes(next.routeId);
    },
  });

  const {
    activeStep,
    steps,
    documents,
    setActiveStep,
    activeDocumentRequest,
    additionalContent,
    allMissingDocuments,
  } = useVerificationCenterHook();
  const [showIntro, setShowIntro] = useState(true);
  const { reportActOnVerificationCenter, reportErrorDuringFileUpload } =
    useVerificationCenterAnalytics();
  const { businessOwners } = useGetBusinessOwners();
  const uboCount = businessOwners.length;
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [sideBarVisible, setSideBarVisible] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<LiliDropdownItem | undefined>(undefined);

  const documentTypeOptionsKey = activeDocumentRequest
    ? documentTypeOptionsKeyByFileType[activeDocumentRequest.fileType]
    : undefined;

  const documentTypeOptions: LiliDropdownItem[] = useMemo(() => {
    if (!documentTypeOptionsKey) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options = t(documentTypeOptionsKey as any, {
      returnObjects: true,
    }) as string[];
    return Array.isArray(options) ? options.map((opt) => ({ title: opt, value: opt })) : [];
  }, [documentTypeOptionsKey]);
  const { debounceAction } = useDebounceHandler();
  const { setFileValue, mutate, handleFileClear, handleFileUpload, fileValue, isLoading } =
    useFileManagement({
      documents,
      setActiveStep,
      reportActOnVerificationCenter,
      reportErrorDuringFileUpload,
      activeDocumentRequest,
    });

  const uploadState = useMemo(() => {
    if (isLoading) return 'uploading' as const;
    if (errorMessage) return 'failed' as const;
    if (fileValue) return 'uploaded' as const;
    return 'default' as const;
  }, [isLoading, errorMessage, fileValue]);

  const isLastStep = activeStep === steps.numberOfSteps - 1;
  const handleContinue = useCallback(
    debounceAction(() => {
      if (activeStep < steps.numberOfSteps - 1) {
        setFileValue(null);
        setSelectedDocType(undefined);
        setActiveStep(activeStep + 1);
      } else {
        mutate();
      }
    }),
    [activeStep, steps.numberOfSteps, setFileValue, setActiveStep, mutate]
  );
  const businessFileTypesImages = getBusinessFileTypesImages(
    activeDocumentRequest?.type === 'NON_US_UBO' ? 'PASSPORT' : steps.activeStepName,
    uboCount
  );

  const handleIntroContinue = useCallback(() => {
    setShowIntro(false);
  }, []);

  if (showIntro) {
    return <IntroScreen onContinue={handleIntroContinue} requiredDocuments={allMissingDocuments} />;
  }

  return (
    <div className='bg-surface-primary-bw-03 flex flex-grow w-full h-full'>
      <div className='w-full bg-text-main-primary-inverse sm:w-1/2 h-full items-center flex flex-col sm:pt-[84px] overflow-hidden border border-outline-primary-bw-03'>
        <div className='flex flex-col w-full max-w-[420px] px-[16px] sm:px-0 flex-shrink-0'>
          <img
            className='mx-auto sm:mx-0 w-[44px] pb-[8px] pt-[8px]'
            src={logoUrl}
            alt='Lili logo'
          />
        </div>
        <span className='w-full border-b border-b-surface-primary-bw-03 mb-[12px] sm:hidden' />
        <div className='flex flex-col w-full max-w-[420px] flex-1 px-[16px] sm:px-0 overflow-y-auto overflow-x-hidden'>
          <div className='flex flex-col flex-1'>
            <VerificationCenterHeader
              activeStep={activeStep}
              docTitle={documents.title}
              numberOfSteps={steps.numberOfSteps}
            />

            {additionalContent && (
              <div className='flex flex-col gap-[0px] mt-[16px] border border-outline-brand-tertiary bg-surface-brand-action-00 px-[16px] py-[12px] rounded-[8px]'>
                <Text.H9
                  fontWeight={FontWeight.MEDIUM}
                  className='text-text-main-primary text-[14px]'>
                  {additionalContent.header}
                </Text.H9>
                <Text.H10 className='text-text-main-primary'>{additionalContent.message}</Text.H10>
                <Text.H10 className='text-text-main-secondary'>
                  {additionalContent.instructions}
                </Text.H10>
              </div>
            )}

            {(documents.texts.positive.length > 0 || documents.texts.negative.length > 0) && (
              <div className='flex flex-col gap-[8px] w-full mb-[12px] mt-[16px]'>
                <div className='text-text-main-secondary text-[14px] leading-[20px]'>
                  {t('pages.verificationCenter.documentMustContain')}
                </div>
                <div className='flex flex-col gap-[4px] w-full'>
                  {documents.texts.positive.map((text, index) => (
                    <div key={`pos-${index}`} className='flex gap-[4px] items-start w-full'>
                      <img src={greenCheckUrl} alt='' className='w-[20px] h-[20px] shrink-0' />
                      <Text.H9 className='text-text-main-secondary text-[14px]'>{text}</Text.H9>
                    </div>
                  ))}
                  {documents.texts.negative.map((text, index) => (
                    <div key={`neg-${index}`} className='flex gap-[4px] items-start w-full'>
                      <img src={greyXUrl} alt='' className='w-[20px] h-[20px] shrink-0' />
                      <Text.H9 className='text-text-main-secondary text-[14px]'>{text}</Text.H9>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className='flex items-center gap-[0px] w-full mb-[24px]'>
              <Text.H9
                fontWeight={FontWeight.SEMIBOLD}
                className='text-text-main-primary text-[14px]'>
                {t('pages.verificationCenter.requiredToApprove')}
              </Text.H9>
            </div>
          </div>

          <div className='flex flex-col w-full flex-1 justify-end'>
            {documentTypeOptions.length > 0 && (
              <div className='flex flex-col gap-[8px]'>
                <Text.H10 className='text-text-main-primary'>
                  {t('pages.verificationCenter.documentType')}
                </Text.H10>
                <LiliDropdown
                  id='document-type-dropdown'
                  title={t('pages.verificationCenter.documentTypeSelect')}
                  items={documentTypeOptions}
                  value={selectedDocType}
                  onSelect={setSelectedDocType}
                  shouldScrollOnOpen
                />
              </div>
            )}

            <div className='mb-[12px] w-full flex-1 max-h-[100px] sm:max-h-[200px]'>
              <FileUpload
                key={activeStep}
                id='requiredDocFileUpload'
                state={uploadState}
                file={fileValue}
                onFileSelect={(file) => {
                  setErrorMessage(undefined);
                  setFileValue(file);
                  handleFileUpload(file, activeDocumentRequest.fileType);
                }}
                onFileClear={() => {
                  setFileValue(null);
                  handleFileClear();
                }}
                onRetry={() => setErrorMessage(undefined)}
                onError={(error) => {
                  if (error === FileUploadError.FILE_TOO_BIG) {
                    setErrorMessage(t('enErrors:filePicker.maxSize', { maxSize: maxFileSizeInMb }));
                  } else if (error === FileUploadError.UNSUPPORTED_TYPE) {
                    setErrorMessage(
                      t('enErrors:filePicker.invalidFileType', { type: 'JPEG, PDF, PNG' })
                    );
                  }
                  setFileValue(null);
                }}
                acceptedTypes={[
                  FileUploadFileType.JPEG,
                  FileUploadFileType.PDF,
                  FileUploadFileType.PNG,
                ]}
                maxFileSizeInMb={maxFileSizeInMb}
              />
            </div>

            {businessFileTypesImages.length > 0 && (
              <div className='flex gap-1 items-center justify-center w-full sm:hidden'>
                <Text.H9 className='text-text-main-secondary text-[14px]'>
                  {t('pages.verificationCenter.theDocument')}
                </Text.H9>
                <Text.H9
                  className='text-text-brand-primary underline cursor-pointer text-[14px]'
                  onClick={() => setSideBarVisible(true)}>
                  {t('pages.verificationCenter.shouldLookLike')}
                </Text.H9>
              </div>
            )}
          </div>
        </div>

        <VerFooter
          onClick={handleContinue}
          text={isLastStep ? t('global.submit') : t('global.FORM_SUBMIT')}
          isLoading={isLoading}
          disabled={!fileValue}
        />
      </div>
      <div className='hidden sm:flex bg-surface-primary-bw-03 flex-1 h-full min-w-px relative rounded-[8px] p-6 flex-col gap-[22px] items-center justify-center overflow-y-auto'>
        {businessFileTypesImages.map((image) => (
          <div key={image} className='w-full flex flex-col items-center rounded-sm'>
            <img
              id='required-doc-example__preview-image'
              src={image}
              alt='Required document example'
              className='contain min-w-[300px] w-full max-w-[500px] h-full object-contain'
            />
          </div>
        ))}
      </div>
      <InfoSideBar
        visible={sideBarVisible}
        onHide={() => setSideBarVisible(false)}
        images={businessFileTypesImages}
      />
    </div>
  );
};

export default VerificationCenter;
